import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { CustomAutocomplete } from "component/shared/auto-complete";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import { getCities, getPages } from "redux/selector";
import { fetchCities } from "redux/slice/cities";
import { fetchPages } from "redux/slice/pages";
import { fetchSearchCities, saveSearchCities } from "redux/slice/search-cities";
import { City } from "model/city";
import { PageType } from "type/page-type";
import { coldFusionReplatform, coldFusionUrl } from "shared/env";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const title = "Search Cities";

const CITY_LIST_FORMAT_OPTIONS = [
	{ value: 1, label: "City" },
	{ value: 2, label: "City, State" },
	{ value: 3, label: "City Sorted By County" },
];

const MAX_AUTOCOMPLETE_RESULTS = 100;

const cityLabel = (city: City) => (city.state ? `${city.name}, ${city.state}` : city.name);

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && (user.permissions.listingSearch || user.permissions.listingMapSearch)}
		title={title}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.Settings />
				</FeatherIcon>
				{title}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/searchCities.cfm" />}
	/>
);

const NativePageBase: React.FC<Props> = ({ user, classes = {} }) => {
	const dispatch = useDispatch();
	const saved = useSelector((state: RootState) => state.searchCities.searchCities);
	const loading = useSelector((state: RootState) => state.searchCities.loading);
	const allCities = useSelector(getCities);
	const pages = useSelector(getPages);

	const [customListEnabled, setCustomListEnabled] = useState(false);
	const [restrictToCustomList, setRestrictToCustomList] = useState(false);
	const [cityListFormat, setCityListFormat] = useState(2);
	const [cityIds, setCityIds] = useState<number[]>([]);
	const [isResponsive, setIsResponsive] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

	useEffect(() => {
		dispatch(fetchSearchCities() as any);
		dispatch(fetchCities() as any);
		dispatch(fetchPages() as any);
	}, [dispatch]);

	useEffect(() => {
		if (saved) {
			setCustomListEnabled(saved.customCityListYn);
			setRestrictToCustomList(saved.restrictToCustomList);
			setCityListFormat(saved.cityListFormat || 2);
			setCityIds(saved.cityIds || []);
			setIsResponsive(saved.isResponsive);
			setDirty(false);
		}
	}, [saved]);

	const citiesById = useMemo(() => {
		const map: { [id: number]: City } = {};
		allCities.forEach((city) => { map[city.id] = city; });
		return map;
	}, [allCities]);

	const selectedCities = useMemo(
		() => cityIds.map((id) => citiesById[id]).filter(Boolean) as City[],
		[cityIds, citiesById]
	);

	// Filter the (potentially large) catalog by the typed input and cap the result count.
	const autocompleteOptions = useMemo(() => {
		const selected = new Set(cityIds);
		const term = inputValue.trim().toLowerCase();
		const available = allCities.filter((city) => !selected.has(city.id));
		const matches = term
			? available.filter((city) => cityLabel(city).toLowerCase().includes(term))
			: available;
		return matches.slice(0, MAX_AUTOCOMPLETE_RESULTS);
	}, [allCities, cityIds, inputValue]);

	const addCity = (city: City | null) => {
		if (city && !cityIds.includes(city.id)) {
			setCityIds((prev) => [...prev, city.id]);
			setDirty(true);
		}
		setInputValue("");
	};

	const removeCity = (id: number) => {
		setCityIds((prev) => prev.filter((cityId) => cityId !== id));
		setDirty(true);
	};

	const clearCustomList = () => {
		setCityIds([]);
		setCustomListEnabled(false);
		setRestrictToCustomList(false);
		setDirty(true);
	};

	const handleSave = async () => {
		// Mirror the ColdFusion validation: an enabled custom list must contain at least one city.
		if (customListEnabled && cityIds.length === 0) {
			setSnackbarMessage("No cities chosen for custom city list");
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
			return;
		}
		setSaving(true);
		try {
			await dispatch(saveSearchCities({
				cityIds,
				customCityListYn: customListEnabled,
				restrictToCustomList: isResponsive && restrictToCustomList,
				cityListFormat,
				isResponsive,
			}) as any);
			setSnackbarMessage("Search city settings saved");
			setSnackbarSeverity("success");
			setSnackbarOpen(true);
			setDirty(false);
		} catch{
			setSnackbarMessage("Failed to save search city settings");
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
		} finally {
			setSaving(false);
		}
	};

	const handleCancel = () => {
		if (saved) {
			setCustomListEnabled(saved.customCityListYn);
			setRestrictToCustomList(saved.restrictToCustomList);
			setCityListFormat(saved.cityListFormat || 2);
			setCityIds(saved.cityIds || []);
		}
		setDirty(false);
	};

	// Link to the client's live search page on their own site (same pattern as
	// settings-search-general): user.baseUrl + the listings/search page path. The legacy
	// CF "/idx/search.cfm?cid=" link only worked because it ran inside the CF app.
	const listingsPage =
		pages?.find((p) => p.key === "LISTING_SEARCH_FORM")
		?? pages?.find((p) => p.type.id === PageType.LISTINGS.id);
	const showFormattedListUrl = listingsPage ? user.baseUrl + listingsPage.path : undefined;
	const printableListUrl = `${coldFusionUrl}/idx/controllers/searchSettingsController.cfc?method=PrintableCityZipList&clientID=${user.clientId}&printListType=1`;

	return (
		<DashboardLayout
			permitted={user && (user.permissions.listingSearch || user.permissions.listingMapSearch)}
			title={title}
			header={<Mui.Typography variant="h1">{title}</Mui.Typography>}
		>
			{loading && <Mui.LinearProgress />}

			<FullPageCard>
				<Mui.Grid container direction="column" spacing={3}>
					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>City List Format</Mui.Typography>
						<Mui.RadioGroup
							value={String(cityListFormat)}
							onChange={(e) => { setCityListFormat(parseInt(e.target.value, 10)); setDirty(true); }}
						>
							{CITY_LIST_FORMAT_OPTIONS.map((opt) => (
								<Mui.FormControlLabel
									key={opt.value}
									value={String(opt.value)}
									control={<Mui.Radio color="secondary" />}
									label={opt.label}
								/>
							))}
						</Mui.RadioGroup>
						{showFormattedListUrl && (
							<Mui.Box className={classes.linkRow}>
								<Mui.Link href={showFormattedListUrl} target="_blank" rel="noopener noreferrer">
									Show Current Formatted City List
								</Mui.Link>
							</Mui.Box>
						)}
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>Use Custom City List</Mui.Typography>
						<Mui.FormControlLabel
							control={
								<Mui.Switch
									color="secondary"
									checked={customListEnabled}
									onChange={(e) => { setCustomListEnabled(e.target.checked); setDirty(true); }}
								/>
							}
							label="Restrict search to a custom list of cities"
						/>
					</Mui.Grid>

					{customListEnabled && (
						<>
							{isResponsive && (
								<Mui.Grid item>
									<Mui.FormControlLabel
										control={
											<Mui.Checkbox
												color="secondary"
												checked={restrictToCustomList}
												onChange={(e) => { setRestrictToCustomList(e.target.checked); setDirty(true); }}
											/>
										}
										label="Restrict Autosuggest & City Selector to Custom City List"
									/>
								</Mui.Grid>
							)}

							<Mui.Grid item>
								<Mui.Typography className={classes.fieldLabel}>Add Cities</Mui.Typography>
								<CustomAutocomplete
									options={autocompleteOptions}
									optionLabelExtractor={(option: City) => cityLabel(option)}
									inputValue={inputValue}
									// Only track the text the user actually types. MUI also fires
									// onInputChange with reason "reset" after a selection (carrying the
									// chosen option's label) — ignoring it lets addCity clear the input.
									onInputChange={(_event, value, reason) => {
										if (reason === "input") {
											setInputValue(value);
										}
									}}
									onChange={(_event, option) => addCity(option as City)}
									placeholder="Search for a city..."
									variant="outlined"
									fullWidth
								/>
								<Mui.Box className={classes.chipContainer}>
									{selectedCities.map((city) => (
										<Mui.Chip
											key={city.id}
											label={cityLabel(city)}
											onDelete={() => removeCity(city.id)}
										/>
									))}
								</Mui.Box>
								<Mui.Box className={classes.linkRow}>
									<Mui.Link component="button" type="button" onClick={clearCustomList}>
										Clear Custom City List
									</Mui.Link>
									<Mui.Link href={printableListUrl} target="_blank" rel="noopener noreferrer">
										Printable List
									</Mui.Link>
								</Mui.Box>
							</Mui.Grid>
						</>
					)}
				</Mui.Grid>
			</FullPageCard>

			<SettingsStickyFooter
				dirty={dirty}
				saving={saving}
				onSave={handleSave}
				onCancel={handleCancel}
			/>

			<Mui.Snackbar
				open={snackbarOpen}
				autoHideDuration={4000}
				onClose={() => setSnackbarOpen(false)}
			>
				<MuiAlert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
					{snackbarMessage}
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const NativePage = withStyles(NativePageBase, styles);

const SettingsSearchCitiesPageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <NativePage {...props} />;
};

export const SettingsSearchCitiesPage = withStyles(SettingsSearchCitiesPageBase, styles);
